/**
 * ThesisConfigSheet — Full-screen config overlay
 *
 * Step 1: Archetype selection grid
 * Step 2: Weight sliders for each dimension
 * Save: local-first (Zustand) + background server sync
 *
 * Inline overlay (no route / no _layout.tsx changes).
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { View, Text, ScrollView, Pressable, type LayoutChangeEvent } from 'react-native';
import {
  X, Check, ChevronRight, AlertTriangle, RotateCcw,
  Users, Lightbulb, Target, TrendingUp, Shield, Swords,
  DollarSign, Clock, Sparkles, Megaphone, Wallet, Zap, Scale,
} from 'lucide-react-native';
import Animated, {
  FadeIn, FadeInDown, FadeInUp, SlideInRight, SlideOutRight,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { DIMENSION_ORDER, DIMENSION_LABELS } from '@/lib/scoring/methodology';
import type { ScoringDimension } from '@/lib/scoring/methodology';
import type { ThesisArchetype } from '@/lib/thesis/types';
import { ARCHETYPE_INFO, WEIGHT_CONSTRAINTS } from '@/lib/thesis/types';
import { getArchetypeWeights } from '@/lib/thesis/archetypes';
import {
  redistributeWeights,
  validateWeights,
  getGuardrailWarnings,
} from '@/lib/thesis/compute';
import { useThesisStore } from '@/lib/thesis/thesis-store';
import { useThesisMutation } from '@/lib/thesis/useThesisSync';
import { handleError } from '@/lib/errors';

// Icon map
const DIM_ICONS: Record<string, typeof TrendingUp> = {
  team: Users, product: Lightbulb, market: Target, traction: TrendingUp,
  defensibility: Shield, competition: Swords, 'business-model': DollarSign,
  timing: Clock, 'vision-story': Sparkles, 'gtm-execution': Megaphone,
  'financial-health': Wallet, 'execution-velocity': Zap, scalability: Scale,
};

interface ThesisConfigSheetProps {
  visible: boolean;
  onClose: () => void;
  investorId: string;
}

export function ThesisConfigSheet({
  visible,
  onClose,
  investorId,
}: ThesisConfigSheetProps) {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const existingThesis = useThesisStore((s) => s.activeThesis);
  const createFromArchetype = useThesisStore((s) => s.createFromArchetype);
  const setThesis = useThesisStore((s) => s.setThesis);
  const thesisMutation = useThesisMutation();

  // Step: 'archetype' → 'weights'
  const [step, setStep] = useState<'archetype' | 'weights'>(
    existingThesis ? 'weights' : 'archetype',
  );
  const [selectedArchetype, setSelectedArchetype] = useState<ThesisArchetype>(
    existingThesis?.archetype ?? 'balanced',
  );
  const [weights, setWeights] = useState<Record<ScoringDimension, number>>(
    existingThesis?.weights ?? getArchetypeWeights('balanced'),
  );
  const [name, setName] = useState<string>(existingThesis?.name ?? '');

  // Derived
  const weightSum = useMemo(
    () => Object.values(weights).reduce((s, v) => s + v, 0),
    [weights],
  );
  const isBalanced = Math.abs(weightSum - 1.0) < WEIGHT_CONSTRAINTS.tolerance;
  const validation = useMemo(() => validateWeights(weights), [weights]);
  const warnings = useMemo(() => getGuardrailWarnings(weights), [weights]);

  // ── Handlers ──

  const handleArchetypeSelect = useCallback((archetype: ThesisArchetype) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedArchetype(archetype);
    setWeights(getArchetypeWeights(archetype));
    const info = ARCHETYPE_INFO.find((a) => a.id === archetype);
    setName(info?.name ?? 'My Thesis');
  }, []);

  const handleArchetypeContinue = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setStep('weights');
  }, []);

  const handleWeightChange = useCallback(
    (dim: ScoringDimension, newValue: number) => {
      setWeights((prev) => {
        const next = redistributeWeights(prev, dim, newValue);
        return next;
      });
      // Mark as custom if modified
      if (selectedArchetype !== 'custom') {
        setSelectedArchetype('custom');
      }
    },
    [selectedArchetype],
  );

  const handleResetToPreset = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setStep('archetype');
  }, []);

  const handleResetEqual = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setWeights(getArchetypeWeights('balanced'));
    setSelectedArchetype('balanced');
    setName('Balanced Generalist');
  }, []);

  const handleSave = useCallback(async () => {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);

    // Local-first: save to Zustand immediately
    const now = new Date().toISOString();
    const thesis = {
      id: existingThesis?.id ?? `local-${Date.now()}`,
      investorId,
      name: name || 'My Thesis',
      archetype: selectedArchetype,
      weights,
      isActive: true,
      createdAt: existingThesis?.createdAt ?? now,
      updatedAt: now,
    };
    setThesis(thesis);
    onClose();

    // Background server sync
    try {
      await thesisMutation.mutateAsync({
        name: thesis.name,
        archetype: thesis.archetype,
        weights: thesis.weights,
      });
    } catch (err) {
      // Non-fatal — local data is saved
      handleError(err, { context: 'ThesisConfigSheet.save' });
    }
  }, [
    existingThesis, investorId, name, selectedArchetype, weights,
    setThesis, onClose, thesisMutation,
  ]);

  if (!visible) return null;

  return (
    <Animated.View
      entering={SlideInRight.duration(350).springify()}
      exiting={SlideOutRight.duration(300)}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.surface.primary,
        zIndex: 100,
      }}
    >
      {/* Header */}
      <View
        style={{
          paddingTop: insets.top + 8,
          paddingHorizontal: 20,
          paddingBottom: 12,
          backgroundColor: colors.surface.primary,
          borderBottomWidth: 1,
          borderBottomColor: colors.border.light,
        }}
      >
        <View className="flex-row items-center justify-between">
          <AnimatedPressable onPress={onClose}>
            <View className="w-9 h-9 rounded-full items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
              <X size={18} color={colors.text.secondary} />
            </View>
          </AnimatedPressable>

          <Text style={{ color: colors.text.primary }} className="text-base font-bold">
            {step === 'archetype' ? 'Choose Style' : 'Configure Weights'}
          </Text>

          {step === 'weights' ? (
            <AnimatedPressable
              onPress={handleSave}
              disabled={!validation.valid}
            >
              <View
                className="px-4 py-2 rounded-full"
                style={{
                  backgroundColor: validation.valid
                    ? colors.primary[500]
                    : colors.light[300],
                }}
              >
                <Text
                  style={{
                    color: validation.valid ? '#fff' : colors.text.tertiary,
                  }}
                  className="text-sm font-semibold"
                >
                  Save
                </Text>
              </View>
            </AnimatedPressable>
          ) : (
            <View className="w-9" />
          )}
        </View>
      </View>

      {/* Content */}
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: insets.bottom + 24 }}
      >
        {step === 'archetype' ? (
          <ArchetypeStep
            selected={selectedArchetype}
            onSelect={handleArchetypeSelect}
            onContinue={handleArchetypeContinue}
            colors={colors}
          />
        ) : (
          <WeightStep
            weights={weights}
            weightSum={weightSum}
            isBalanced={isBalanced}
            warnings={warnings}
            onWeightChange={handleWeightChange}
            onResetToPreset={handleResetToPreset}
            onResetEqual={handleResetEqual}
            colors={colors}
          />
        )}
      </ScrollView>
    </Animated.View>
  );
}

// =============================================================================
// Step 1: Archetype Selection
// =============================================================================

function ArchetypeStep({
  selected,
  onSelect,
  onContinue,
  colors,
}: {
  selected: ThesisArchetype;
  onSelect: (a: ThesisArchetype) => void;
  onContinue: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="px-5 pt-5">
      <Animated.View entering={FadeInDown.delay(100).duration(400)}>
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold mb-1">
          Investment Style
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-6">
          Pick a starting point. You can fine-tune individual weights next.
        </Text>
      </Animated.View>

      <View className="gap-3">
        {ARCHETYPE_INFO.map((arch, index) => {
          const isSelected = selected === arch.id;
          return (
            <Animated.View
              key={arch.id}
              entering={FadeInDown.delay(150 + index * 60).duration(350)}
            >
              <AnimatedPressable onPress={() => onSelect(arch.id)}>
                <View
                  className="rounded-2xl p-4 flex-row items-center gap-4"
                  style={{
                    backgroundColor: isSelected
                      ? colors.primary[50]
                      : colors.surface.primary,
                    borderWidth: isSelected ? 2 : 1,
                    borderColor: isSelected
                      ? colors.primary[500]
                      : colors.border.light,
                  }}
                >
                  {/* Icon */}
                  <View
                    className="w-12 h-12 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor: isSelected
                        ? colors.primary[100]
                        : colors.surface.secondary,
                    }}
                  >
                    <Text className="text-lg">
                      {arch.emoji}
                    </Text>
                  </View>

                  {/* Text */}
                  <View className="flex-1">
                    <Text
                      style={{
                        color: isSelected ? colors.primary[700] : colors.text.primary,
                      }}
                      className="text-base font-semibold"
                    >
                      {arch.name}
                    </Text>
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs mt-0.5"
                    >
                      {arch.description}
                    </Text>
                  </View>

                  {/* Check */}
                  {isSelected && (
                    <View
                      className="w-6 h-6 rounded-full items-center justify-center"
                      style={{ backgroundColor: colors.primary[500] }}
                    >
                      <Check size={14} color="#fff" strokeWidth={3} />
                    </View>
                  )}
                </View>
              </AnimatedPressable>
            </Animated.View>
          );
        })}
      </View>

      {/* Continue button */}
      <Animated.View entering={FadeInUp.delay(500).duration(400)} className="mt-8">
        <AnimatedPressable onPress={onContinue}>
          <View
            className="rounded-2xl py-4 flex-row items-center justify-center gap-2"
            style={{ backgroundColor: colors.primary[500] }}
          >
            <Text className="text-base font-bold" style={{ color: '#fff' }}>
              Customize Weights
            </Text>
            <ChevronRight size={18} color="#fff" />
          </View>
        </AnimatedPressable>
      </Animated.View>
    </View>
  );
}

// =============================================================================
// Step 2: Weight Sliders
// =============================================================================

function WeightStep({
  weights,
  weightSum,
  isBalanced,
  warnings,
  onWeightChange,
  onResetToPreset,
  onResetEqual,
  colors,
}: {
  weights: Record<ScoringDimension, number>;
  weightSum: number;
  isBalanced: boolean;
  warnings: string[];
  onWeightChange: (dim: ScoringDimension, value: number) => void;
  onResetToPreset: () => void;
  onResetEqual: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="pt-4">
      {/* Running total */}
      <Animated.View entering={FadeIn.duration(300)} className="px-5 mb-4">
        <View
          className="rounded-xl p-3 flex-row items-center justify-between"
          style={{
            backgroundColor: isBalanced ? colors.traffic.greenLight : colors.amber.light,
            borderWidth: 1,
            borderColor: isBalanced ? `${colors.success}30` : `${colors.warning}30`,
          }}
        >
          <Text
            style={{
              color: isBalanced ? colors.success : colors.warning,
            }}
            className="text-sm font-semibold"
          >
            Total: {(weightSum * 100).toFixed(1)}%
          </Text>
          {isBalanced ? (
            <View className="flex-row items-center gap-1">
              <Check size={14} color={colors.success} strokeWidth={3} />
              <Text style={{ color: colors.success }} className="text-xs font-medium">
                Balanced
              </Text>
            </View>
          ) : (
            <Text style={{ color: colors.warning }} className="text-xs font-medium">
              Adjusting...
            </Text>
          )}
        </View>
      </Animated.View>

      {/* Reset buttons */}
      <Animated.View entering={FadeIn.duration(300)} className="px-5 mb-3">
        <View className="flex-row gap-2">
          <Pressable onPress={onResetToPreset} style={{ flex: 1 }}>
            <View
              className="flex-row items-center justify-center gap-1.5 py-2 rounded-full"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <RotateCcw size={12} color={colors.text.secondary} />
              <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                Reset to Preset
              </Text>
            </View>
          </Pressable>
          <Pressable onPress={onResetEqual} style={{ flex: 1 }}>
            <View
              className="flex-row items-center justify-center gap-1.5 py-2 rounded-full"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Scale size={12} color={colors.text.secondary} />
              <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                Reset to Equal
              </Text>
            </View>
          </Pressable>
        </View>
      </Animated.View>

      {/* Guardrail warnings */}
      {warnings.length > 0 && (
        <Animated.View entering={FadeIn.duration(300)} className="px-5 mb-3">
          <View
            className="rounded-xl p-3 gap-1.5"
            style={{ backgroundColor: colors.amber.light }}
          >
            {warnings.map((w, i) => (
              <View key={i} className="flex-row items-start gap-2">
                <AlertTriangle size={12} color={colors.warning} style={{ marginTop: 1 }} />
                <Text style={{ color: colors.warning }} className="text-xs flex-1">
                  {w}
                </Text>
              </View>
            ))}
          </View>
        </Animated.View>
      )}

      {/* Dimension sliders */}
      <View className="gap-0.5">
        {DIMENSION_ORDER.map((dim, index) => (
          <WeightSliderRow
            key={dim}
            dimension={dim}
            weight={weights[dim]}
            index={index}
            onValueChange={(val) => onWeightChange(dim, val)}
            colors={colors}
          />
        ))}
      </View>
    </View>
  );
}

// =============================================================================
// Individual Weight Slider Row
// =============================================================================

function WeightSliderRow({
  dimension,
  weight,
  index,
  onValueChange,
  colors,
}: {
  dimension: ScoringDimension;
  weight: number;
  index: number;
  onValueChange: (value: number) => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const Icon = DIM_ICONS[dimension] || TrendingUp;
  const label = DIMENSION_LABELS[dimension];
  const pct = weight * 100;
  const { min, max } = WEIGHT_CONSTRAINTS;
  const minPct = min * 100;
  const maxPct = max * 100;

  // Fill percentage for the track
  const fillPct = ((pct - minPct) / (maxPct - minPct)) * 100;

  const neutral = 100 / DIMENSION_ORDER.length;
  const isAboveNeutral = pct > neutral + 0.5;
  const isBelowNeutral = pct < neutral - 0.5;

  const stepDown = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onValueChange(Math.max(min, weight - 0.005));
  }, [weight, min, onValueChange]);

  const stepUp = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onValueChange(Math.min(max, weight + 0.005));
  }, [weight, max, onValueChange]);

  // Track width measured via onLayout
  const trackWidthRef = useRef<number>(0);

  const handleTrackLayout = useCallback((e: LayoutChangeEvent) => {
    trackWidthRef.current = e.nativeEvent.layout.width;
  }, []);

  const locationToValue = useCallback(
    (locationX: number) => {
      const w = trackWidthRef.current;
      if (w <= 0) return;
      const ratio = Math.max(0, Math.min(1, locationX / w));
      const newPct = minPct + ratio * (maxPct - minPct);
      onValueChange(newPct / 100);
    },
    [minPct, maxPct, onValueChange],
  );

  return (
    <Animated.View entering={FadeInDown.delay(50 + index * 25).duration(300)}>
      <View
        className="px-5 py-2.5"
        style={{
          backgroundColor: isAboveNeutral
            ? `${colors.primary[500]}06`
            : colors.surface.primary,
        }}
      >
        {/* Label row */}
        <View className="flex-row items-center gap-2.5 mb-1.5">
          <Icon
            size={14}
            color={isAboveNeutral ? colors.primary[600] : colors.text.tertiary}
          />
          <Text
            style={{
              color: isAboveNeutral ? colors.primary[700] : colors.text.primary,
              fontWeight: isAboveNeutral ? '600' : '500',
            }}
            className="text-sm flex-1"
          >
            {label}
          </Text>
          <Text
            style={{
              color: isAboveNeutral
                ? colors.primary[600]
                : isBelowNeutral
                  ? colors.text.tertiary
                  : colors.text.secondary,
              fontVariant: ['tabular-nums'],
            }}
            className="text-sm font-bold"
          >
            {pct.toFixed(1)}%
          </Text>
        </View>

        {/* Slider row: [-] [====track====] [+] */}
        <View className="flex-row items-center gap-2">
          {/* Minus button */}
          <Pressable
            onPress={stepDown}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: colors.surface.secondary,
              justifyContent: 'center',
              alignItems: 'center',
            }}
            hitSlop={6}
          >
            <Text style={{ color: colors.text.secondary, fontSize: 16, fontWeight: '700', lineHeight: 18 }}>
              -
            </Text>
          </Pressable>

          {/* Track — uses responder system for drag */}
          <View
            className="flex-1"
            style={{ height: 36, justifyContent: 'center' }}
            onLayout={handleTrackLayout}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={(e) => locationToValue(e.nativeEvent.locationX)}
            onResponderMove={(e) => locationToValue(e.nativeEvent.locationX)}
          >
            {/* Track background */}
            <View
              className="rounded-full overflow-hidden"
              style={{ height: 6, backgroundColor: colors.surface.tertiary }}
              pointerEvents="none"
            >
              <View
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, Math.max(0, fillPct))}%`,
                  backgroundColor: isAboveNeutral
                    ? colors.primary[500]
                    : colors.light[500],
                }}
              />
            </View>

            {/* Thumb */}
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: `${Math.min(97, Math.max(3, fillPct))}%`,
                top: (36 - 20) / 2,
                width: 20,
                height: 20,
                borderRadius: 10,
                marginLeft: -10,
                backgroundColor: isAboveNeutral
                  ? colors.primary[500]
                  : colors.light[600],
                borderWidth: 2,
                borderColor: '#fff',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.2,
                shadowRadius: 3,
                elevation: 4,
              }}
            />
          </View>

          {/* Plus button */}
          <Pressable
            onPress={stepUp}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: colors.surface.secondary,
              justifyContent: 'center',
              alignItems: 'center',
            }}
            hitSlop={6}
          >
            <Text style={{ color: colors.text.secondary, fontSize: 16, fontWeight: '700', lineHeight: 18 }}>
              +
            </Text>
          </Pressable>
        </View>
      </View>
    </Animated.View>
  );
}
